import React from 'react';
import { ModeContext } from './ModeContext';
import { fetchRecentDocumentsForTags } from '@/utils/doc';
import { logError } from '@utils/usefulFuncs';

const initialState = {
  question: '',
  agentName: '',
  tags: [],
  selectedDocs: [],
  selectedCsvDocs: [],
  noRetrieval: false,
  onlySources: [],
  excludeSources: [],
  invalidCsvDocIds: [],
  csvErrorByDocId: {},
};

const defaultContextValue = {
  state: initialState,
  setFormStateValue: () => logError('setFormStateValue not initialized'),
};

const ACTIONS = {
  SET_VALUE: 'SET_VALUE',
  ADD_SELECTED_DOCS: 'ADD_SELECTED_DOCS',
  RESET: 'RESET',
};

const reducer = (state, action) => {
  switch (action.type) {
    case ACTIONS.SET_VALUE: {
      const { key, value } = action.payload;
      return { ...state, [key]: value };
    }
    case ACTIONS.ADD_SELECTED_DOCS: {
      return { ...state, selectedDocs: [...state.selectedDocs, ...action.value] };
    }
    case ACTIONS.RESET: {
      return action.value;
    }
    default: {
      return state;
    }
  }
};

function filterForNewTags(newTags, oldTags) {
  const companyTags = newTags.filter((tag) => tag.doc_tag_id !== null);
  const publicTags = newTags.filter((tag) => tag.company_id !== null);

  const oldCompanyTags = oldTags.filter((tag) => tag.doc_tag_id !== null);
  const oldPublicTags = oldTags.filter((tag) => tag.company_id !== null);

  const newCompanyTags = companyTags.filter((tag) => !oldCompanyTags.some((t) => t.doc_tag_id === tag.doc_tag_id));
  const newPublicTags = publicTags.filter((tag) => !oldPublicTags.some((t) => t.company_id === tag.company_id));

  return [...newCompanyTags, ...newPublicTags];
}

export const FormStateContext = React.createContext(defaultContextValue);

export const FormStateProvider = ({ initialStateParam, children }) => {
  const { autoSelectRecentDocuments } = React.useContext(ModeContext);
  const initialFormState = React.useMemo(() => initialStateParam || initialState, [initialStateParam]);
  const [state, dispatch] = React.useReducer(reducer, initialFormState);

  const setFormStateValue = React.useCallback(
    async (key, value) => {
      if (key === null) {
        return dispatch({ type: ACTIONS.RESET, value: initialFormState });
      }
      dispatch({ type: ACTIONS.SET_VALUE, payload: { key, value } });

      if (key === 'tags' && autoSelectRecentDocuments) {
        const newTags = filterForNewTags(value, state.tags);
        const recentDocs = await fetchRecentDocumentsForTags(newTags);
        dispatch({ type: ACTIONS.ADD_SELECTED_DOCS, value: recentDocs });
      }
    },
    [autoSelectRecentDocuments, dispatch, initialFormState, state.tags]
  );

  const value = React.useMemo(() => ({ state, setFormStateValue }), [state, setFormStateValue]);
  return <FormStateContext.Provider value={value}>{children}</FormStateContext.Provider>;
};
